import { useEffect, useMemo, useState } from 'react';
import {
  detectMobileDevice,
  readMobileDetectionInput,
} from '@x9/web-device';
import X9Logo from '../components/X9Logo';
import { invalidateBillingSummaryCache } from '../hooks/useBillingSummary';
import { NATIVE_APP_UNIVERSAL, openNativeApp } from '../lib/openNativeApp';
import { fetchBillingSubscription } from '../services/api';

type Phase = 'waiting' | 'active' | 'slow';

type Props = {
  subscriptionId: string;
  onViewBilling: () => void;
};

export default function PendingPage({ subscriptionId, onViewBilling }: Props) {
  const [phase, setPhase] = useState<Phase>('waiting');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const isMobile = useMemo(
    () => detectMobileDevice(readMobileDetectionInput()),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    let ticks = 0;

    const poll = async () => {
      try {
        const data = await fetchBillingSubscription();
        if (cancelled) return;
        if (data.hasPaidAccess) {
          invalidateBillingSummaryCache();
          setPhase('active');
          return;
        }
      } catch {
        /* keep waiting */
      }
      ticks += 1;
      if (!cancelled && ticks >= 10) setPhase((p) => (p === 'waiting' ? 'slow' : p));
    };

    void poll();
    const id = window.setInterval(() => void poll(), 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    let cancelled = false;
    import('qrcode')
      .then(({ default: QRCode }) =>
        QRCode.toDataURL(NATIVE_APP_UNIVERSAL, {
          width: 240,
          margin: 2,
          color: { dark: '#0a2540', light: '#ffffff' },
        }),
      )
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isMobile]);

  const waiting = phase === 'waiting' || phase === 'slow';
  const title =
    phase === 'active'
      ? 'Pagamento confirmado'
      : phase === 'slow'
        ? 'Ainda confirmando…'
        : 'Confirmando pagamento';
  const body =
    phase === 'active'
      ? 'Seu plano está ativo. Volte ao app — sua conta agora tem retenção estendida e acesso contínuo.'
      : phase === 'slow'
        ? 'A Asaas ainda está processando. Você pode acompanhar em Meu plano — o app libera o acesso assim que confirmar.'
        : 'Estamos liberando seu acesso. Isso costuma levar só alguns segundos.';

  return (
    <div className="my-shell items-center gap-6 text-center">
      <div
        className={`w-[min(56vw,12rem)] text-foreground ${
          waiting ? 'portal-loader__logo--float' : ''
        }`}
      >
        <X9Logo variant="wordmark" title="X9" className="block h-auto w-full select-none" />
      </div>

      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">{body}</p>
      </header>

      {waiting && subscriptionId ? (
        <p className="text-xs text-muted-foreground">
          Ref. <span className="font-mono">{subscriptionId.slice(0, 8)}…</span>
        </p>
      ) : null}

      {isMobile ? (
        <div className="flex w-full max-w-sm flex-col gap-2">
          <button
            type="button"
            className={phase === 'active' ? 'my-btn-primary w-full' : 'my-btn-secondary w-full'}
            onClick={openNativeApp}
          >
            {phase === 'active' ? 'Voltar ao app X9' : 'Abrir o app X9'}
          </button>
          <button
            type="button"
            className={phase === 'active' ? 'my-btn-secondary w-full' : 'my-btn-primary w-full'}
            onClick={onViewBilling}
          >
            Ver meu plano
          </button>
        </div>
      ) : (
        <div className="my-card w-full max-w-sm space-y-4">
          <p className="text-sm font-medium text-foreground">
            {phase === 'active'
              ? 'Escaneie para abrir o X9 no celular'
              : 'No celular? Escaneie para abrir o app'}
          </p>
          <div className="mx-auto w-fit rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/5">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                width={240}
                height={240}
                alt="QR code para abrir o app X9"
                className="block h-48 w-48"
              />
            ) : (
              <div className="flex h-48 w-48 items-center justify-center text-sm text-muted-foreground">
                Gerando QR…
              </div>
            )}
          </div>
          <button type="button" className="my-btn-secondary w-full" onClick={onViewBilling}>
            Ver meu plano
          </button>
        </div>
      )}
    </div>
  );
}
